// @ts-check
import React, { useLayoutEffect, useRef } from "react"
import Button from "../../../../../components/atoms/Button.atom"
import Card from "../../../../../components/atoms/Card.atom"
import InputTextarea from "../../../../../components/atoms/InputTextarea.atom"
import { clsx } from "../../../../../utils/strings.utils"
import { ESTIMATION_AGENT_FILE_INPUT_ACCEPT } from "./estimationAgentAttachments.constants"
import { isOnlyFilesystemUriPasteText } from "./estimationAgentClipboard.utils"
import ComposerAttachmentStrip from "./ComposerAttachmentStrip"

/**
 * @param {File[]} files
 * @returns {File[]}
 */
function dedupeFilesByIdentity(files) {
  const seen = new Set()
  /** @type {File[]} */
  const out = []
  for (const f of files) {
    const key = `${f.name}\0${f.size}\0${f.lastModified}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(f)
  }
  return out
}

/**
 * Si existen `files` en el DataTransfer, usar solo esos: combinar con `items` duplica
 * capturas (mismo blob en FileList y en items).
 *
 * @param {{ clipboardData: DataTransfer | null }} e
 * @returns {File[]}
 */
function filesFromClipboardEvent(e) {
  const cd = e.clipboardData
  if (!cd) return []
  if (cd.files?.length) {
    return dedupeFilesByIdentity(Array.from(cd.files))
  }
  /** @type {File[]} */
  const fromItems = []
  if (cd.items?.length) {
    for (let i = 0; i < cd.items.length; i++) {
      const item = cd.items[i]
      if (item.kind !== "file") continue
      const f = item.getAsFile()
      if (f) fromItems.push(f)
    }
  }
  return dedupeFilesByIdentity(fromItems)
}

/**
 * Texto útil para detectar pegado solo-URI (GTK a veces no rellena `text/plain` en el evento).
 *
 * @param {DataTransfer | null} cd
 */
function clipboardUriTextCandidate(cd) {
  if (!cd) return ""
  let s = cd.getData("text/plain").trim()
  if (s) return s
  s = cd.getData("text/uri-list").trim()
  if (s) {
    return s
      .split(/\r?\n/)
      .map((line) => {
        const hash = line.indexOf("#")
        return (hash >= 0 ? line.slice(0, hash) : line).trim()
      })
      .filter(Boolean)
      .join("\n")
  }
  const gnome = cd.getData("x-special/gnome-copied-files").trim()
  if (gnome) {
    const lines = gnome.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
    if (lines.length && (lines[0] === "copy" || lines[0] === "cut")) {
      return lines.slice(1).join("\n")
    }
    return gnome
  }

  const moz = cd.getData("text/x-moz-url").trim()
  if (moz) {
    const first = moz.split(/\r?\n/)[0]?.trim() ?? ""
    if (/^(recent|file):\/\//i.test(first)) return first
  }

  if (cd.types?.length) {
    for (let i = 0; i < cd.types.length; i++) {
      const type = cd.types[i]
      if (type === "text/html") continue
      if (!type.startsWith("text/") && type !== "x-special/gnome-copied-files")
        continue
      let t = ""
      try {
        t = cd.getData(type).trim()
      } catch {
        continue
      }
      if (!t) continue
      const lines = t
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
      if (
        lines.length > 0 &&
        lines.every((line) => /^(recent|file):\/\//i.test(line))
      ) {
        return t
      }
    }
  }

  return ""
}

/**
 * Portapapeles con solo URIs `recent:` / `file:` sin objeto File.
 *
 * @param {DataTransfer | null} cd
 */
function isFilesystemUriOnlyClipboardText(cd) {
  if (!cd) return false
  return isOnlyFilesystemUriPasteText(clipboardUriTextCandidate(cd))
}

/** @type {import("react").ComponentType<Record<string, unknown>>} */
const InputTextareaLoose = InputTextarea

/**
 * @typedef {object} ComposerProps
 * @property {string} value
 * @property {(e: import("react").ChangeEvent<HTMLTextAreaElement>) => void} onChange
 * @property {() => void} onSend
 * @property {() => void} onStop
 * @property {boolean} [isCreatingChat]
 * @property {boolean} [isResponseStreaming]
 * @property {boolean} [isLoadingChatDetail]
 * @property {boolean} [composerLocked]
 * @property {boolean} [isComposerUploading]
 * @property {boolean} [hasReadyAttachments]
 * @property {Array<{
 *   id: string;
 *   phase: "uploading" | "ready" | "error";
 *   localName: string;
 *   mediaType: string;
 *   url?: string;
 *   error?: string;
 * }>} [attachments]
 * @property {(id: string) => void} [onRemoveAttachment]
 * @property {(files: FileList | File[]) => void} [onFilesSelected]
 * @property {() => void} [onPasteFilesystemUriOnly]
 * @property {string} [placeholder]
 * @property {string} [className]
 */

/**
 * @param {ComposerProps} props
 */
export default function Composer({
  value,
  onChange,
  onSend,
  onStop,
  onFilesSelected,
  onPasteFilesystemUriOnly,
  onRemoveAttachment,
  attachments = [],
  placeholder = "Cuentame la idea...",
  className,
  isCreatingChat = false,
  isResponseStreaming = false,
  isLoadingChatDetail = false,
  composerLocked = false,
  isComposerUploading = false,
  hasReadyAttachments = false,
}) {
  const textareaRef = useRef(/** @type {HTMLTextAreaElement | null} */ (null))
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null))

  useLayoutEffect(() => {
    if (value !== "") return
    const el = textareaRef.current
    if (!el) return
    const prevMinHeight = el.style.minHeight
    el.style.minHeight = "0"
    el.style.height = "0px"
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight
    el.style.height = `${el.scrollHeight}px`
    el.style.minHeight = prevMinHeight
  }, [value])

  /**
   * @param {import("react").KeyboardEvent<HTMLTextAreaElement>} e
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      if (isResponseStreaming) {
        onStop()
      } else if (
        !isCreatingChat &&
        !isLoadingChatDetail &&
        !composerLocked &&
        !isComposerUploading
      ) {
        onSend()
      }
    }
  }

  /**
   * @param {import("react").ClipboardEvent<HTMLTextAreaElement>} e
   */
  const handlePaste = (e) => {
    const files = filesFromClipboardEvent(e)
    if (files.length > 0 && onFilesSelected) {
      e.preventDefault()
      onFilesSelected(files)
      return
    }

    if (isFilesystemUriOnlyClipboardText(e.clipboardData)) {
      e.preventDefault()
      onPasteFilesystemUriOnly?.()
    }
  }

  const primaryDisabled =
    isResponseStreaming ?
      false
    : ((!value.trim() && !hasReadyAttachments) ||
        isCreatingChat ||
        isLoadingChatDetail ||
        composerLocked ||
        isComposerUploading)

  const handlePrimaryClick = () => {
    if (isResponseStreaming) {
      onStop()
    } else {
      onSend()
    }
  }

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  /**
   * @param {import("react").ChangeEvent<HTMLInputElement>} e
   */
  const handleFileInputChange = (e) => {
    const { files } = e.target
    if (files && files.length > 0 && onFilesSelected) {
      onFilesSelected(files)
    }
    e.target.value = ""
  }

  return (
    <div className={clsx("estimation-agent-quick-v3__toolbox", className)}>
      <input
        ref={fileInputRef}
        type="file"
        className="estimation-agent-quick-v3__file-input-hidden"
        accept={ESTIMATION_AGENT_FILE_INPUT_ACCEPT}
        multiple
        aria-hidden
        tabIndex={-1}
        onChange={handleFileInputChange}
      />
      <div className="estimation-agent-quick-v3__toolbox-inner">
        <Card className="estimation-agent-quick-v3__toolbox-card shadow-none">
          <ComposerAttachmentStrip
            items={attachments}
            onRemove={onRemoveAttachment ?? (() => {})}
          />
          <div className="estimation-agent-quick-v3__toolbox-row">
            <div className="estimation-agent-quick-v3__toolbox-field">
              <InputTextareaLoose
                ref={textareaRef}
                placeholder={placeholder}
                autoResize
                rows={1}
                className="w-full"
                value={value}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                disabled={isLoadingChatDetail || composerLocked}
              />
            </div>
          </div>
          <div className="estimation-agent-quick-v3__toolbox-actions">
            <Button
              type="button"
              icon="pi pi-plus"
              className="p-button-sm p-button-secondary estimation-agent-quick-v3__attach"
              onClick={openFilePicker}
              aria-label="Agregar archivo"
              disabled={composerLocked || isComposerUploading}
            />

            <Button
              type="button"
              icon={isResponseStreaming ? "pi pi-stop" : "pi pi-arrow-up"}
              className="p-button-sm estimation-agent-quick-v3__send"
              disabled={primaryDisabled}
              onClick={handlePrimaryClick}
              aria-label={
                isResponseStreaming ? "Detener generación" : "Enviar"
              }
            />
          </div>
        </Card>
      </div>
    </div>
  )
}
